import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { MdDragIndicator, MdEdit, MdDelete, MdLock } from 'react-icons/md'
import { Task } from './types'

type CompanyMap = Record<number, string>

type TaskCardProps = {
  task: Task
  companyMap: CompanyMap
  onEdit: (task: Task) => void
  onDelete: (taskId: string) => void
}

export const TaskCard = ({
  task,
  companyMap,
  onEdit,
  onDelete
}: TaskCardProps) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging
  } = useSortable({ id: task.id, data: { type: 'Task', task } })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const isOverdue =
    task.dueDate !== '' &&
    new Date(task.dueDate) < new Date(new Date().toDateString())

  const formatDueDate = (dueDate: string) => {
    if (dueDate === '') return ''

    const date = new Date(dueDate)
    if (Number.isNaN(date.getTime())) {
      return dueDate.replace(/-/g, '/')
    }

    const year = String(date.getFullYear())
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return year + '/' + month + '/' + day
  }

  if (isDragging) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="h-[90px] rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 opacity-50"
      />
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/card rounded-lg border bg-white p-3 shadow-sm ${
        task.isSecret
          ? 'border-purple-200 bg-purple-50'
          : 'border-gray-200'
      } hover:shadow-md`}
    >
      <div className="flex items-start gap-2">
        <div
          {...attributes}
          {...listeners}
          className="mt-0.5 flex-shrink-0 cursor-grab text-gray-400 hover:text-gray-600"
        >
          <MdDragIndicator size={18} />
        </div>
        <div className="flex flex-1 items-center gap-1.5 overflow-hidden">
          {task.isSecret && (
            <MdLock size={18} className="flex-shrink-0 text-yellow-400" />
          )}
          <span className="truncate text-sm font-semibold text-gray-800">
            {task.title}
          </span>
        </div>
        <div className="flex flex-shrink-0 gap-1">
          <button
            type="button"
            onClick={() => onEdit(task)}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-[#01695C]"
          >
            <MdEdit size={18} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(task.id)}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
          >
            <MdDelete size={18} />
          </button>
        </div>
      </div>
      {task.companyId && companyMap[task.companyId] && (
        <div className="mt-1.5 pl-5">
          <span className="flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">
            {companyMap[task.companyId]}
          </span>
        </div>
      )}
      {task.description && (
        <p className="mt-1.5 truncate pl-5 text-xs text-gray-500">
          {task.description}
        </p>
      )}

      {task.dueDate && (
        <div className="mt-2 pl-5">
          <span
            className={`text-xs ${
              isOverdue ? 'font-semibold text-red-500' : 'text-gray-500'
            }`}
          >
            {'期限: ' + formatDueDate(task.dueDate)}
          </span>
        </div>
      )}
    </div>
  )
}
